/**
 * Captain Bram in the guide's DOM: `mountBram` puts him in a slot (the bubble's bust, the welcome card's figure, the
 * Guide sheet's replay card). The slot always holds the static render first; when he can be live (bramFallback.ts)
 * the shared stage draws him over it and the still hides. The overlay tells the handle where the step points, what
 * he is saying, and when the player tapped outside the spotlight.
 */
import { tierBand } from "../../../render/quality";
import { appliedTier } from "../../../render/graphicsPrefs";
import { uiUrl } from "../../portraits";
import type { GuideKind, GuideStep } from "../guide";
import { MOOD_ART, moodFor, type BramMood } from "./bramBrain";
import type { BramArtPose } from "./bramContract";
import { bramMode, staticArt, type BramEnv, type TierBandName } from "./bramFallback";
import type { Speech } from "./bramSpeech";
import { BramStage, type BramFraming, type BramSlot } from "./bramStage";

export interface BramHandle {
  /** Viewport CSS px of the step's target, or null (he looks at the player). */
  look(target: { x: number; y: number } | null): void;
  say(speech: Speech | null): void;
  /** The player tapped outside the spotlight. */
  nudge(): void;
  /** Something changed on screen (the overlay's refresh): resume drawing if he had stopped. */
  poke(): void;
  /** The step is over. `leaving`: its card is fading out, so he stays as he is (no still popping back in). */
  release(leaving?: boolean): void;
}

const band = (): TierBandName => { try { return tierBand(appliedTier()).name; } catch { return "medium"; } };
const dpr = (): number => (typeof devicePixelRatio === "number" ? devicePixelRatio : 1);

let stage: BramStage | null = null;
function theStage(): BramStage { return (stage ??= new BramStage({ band, dpr })); }

function env(): BramEnv {
  const reducedMotion = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const reducedVfx = typeof document !== "undefined" && document.body?.classList.contains("reduced-vfx") === true;
  return { reducedMotion, reducedVfx, band: band(), failed: stage?.failed ?? false };
}

/** Is he drawn live right now (for the typewriter's and tests' sake)? */
export function bramLive(): boolean { return bramMode(env()) === "live"; }

/** Starts loading the model early (a first session, before the welcome card), when he will be drawn live. */
export function preloadBram(): void { if (bramLive()) theStage().preload(); }

/**
 * The still for a pose, `cssPx` tall: the new render at the size it needs, falling back file by file (the old
 * busts stand in until the new renders ship).
 */
export function bramStill(pose: BramArtPose, cssPx: number): HTMLImageElement {
  const img = document.createElement("img");
  img.decoding = "async";
  img.alt = "";
  img.draggable = false;
  img.className = "g-still";
  const tries = staticArt(pose, cssPx, dpr()).map((p) => uiUrl(p));
  let i = 0;
  img.onerror = () => { if (++i < tries.length) img.src = tries[i]; else img.onerror = null; };
  img.src = tries[0];
  return img;
}

/** The still that stands for a step. */
export function stepStill(step: Pick<GuideStep, "kind" | "pose" | "priority" | "screen">, cssPx: number): HTMLImageElement {
  return bramStill(MOOD_ART[moodFor(step)], cssPx);
}

/** Fetches the stills a first session shows, so a pose change never pops in. */
export function preloadStills(cssPx: number): void {
  for (const pose of ["neutral", "pointing", "cheering", "wave"] as const) bramStill(pose, cssPx);
}

export interface MountOptions {
  readonly framing: BramFraming;
  readonly mood: BramMood;
  readonly kind: GuideKind;
  /** The still's drawn height (CSS px), for picking its file. */
  readonly cssPx: number;
}

/**
 * Shows him in `slot` (emptied first): the still at once, and live over it when he can be. The still stays in the
 * slot as the fallback for a lost context.
 */
export function mountBram(slot: HTMLElement, o: MountOptions): BramHandle {
  slot.replaceChildren(bramStill(MOOD_ART[o.mood], o.cssPx));
  slot.classList.remove("live", "mirror");
  if (bramMode(env()) !== "live") return { look() {}, say() {}, nudge() {}, poke() {}, release() {} };
  const s = theStage();
  let released = false;
  let frozen = false;
  const state: BramSlot = {
    el: slot, framing: o.framing, mood: o.mood, kind: o.kind, target: null, speech: null,
    live: (on) => { if (!frozen) slot.classList.toggle("live", on); },
    mirror: (on) => slot.classList.toggle("mirror", on),
  };
  // M45 ("looks like it double renders"): with the stage already warm he is drawn within two frames, so the still never
  // shows under him: a still cross-fading into the live bust at another pose read as two captains on every line.
  if (s.warm) slot.classList.add("live", "instant");
  s.attach(state);
  return {
    look(t) { if (!released) state.target = t; },
    say(sp) { if (!released) { state.speech = sp; s.poke(); } },
    nudge() { if (!released) s.nudge(); },
    poke() { if (!released) s.poke(); },
    release(leaving = false) { if (released) return; released = true; frozen = leaving; s.detach(state); },
  };
}

/** For captures: the stage's state (`__td.guide.bram` in ?debug). */
export function bramDebug(): { live: boolean; failed: boolean; renders: number; model: string | null } {
  return { live: bramLive(), failed: stage?.failed ?? false, renders: stage?.renders ?? 0, model: stage?.modelName ?? null };
}

/** For captures: the next frame he draws, as a PNG data URL (null if he is not drawing within `ms`). */
export function bramSnap(ms = 2000): Promise<string | null> {
  const s = stage;
  if (!s) return Promise.resolve(null);
  return new Promise((resolve) => {
    const t = setTimeout(() => { s.snap = null; resolve(null); }, ms);
    s.snap = (png) => { clearTimeout(t); resolve(png); };
    s.poke();
  });
}
